/**
 * ESS Climate Intelligence — Hero Section
 * Live ENSO status cards + animated ocean background
 */

import { fetchCurrentStatus, longDate } from '../data/noaa.js';
import { infoTrigger, infoPanel, setInfoMeaning } from './InfoNote.js';

export function createHeroSection() {
  const section = document.createElement('section');
  section.id = 'hero';
  section.className = 'hero';
  section.innerHTML = `
    <div class="hero__bg">
      <div class="hero__ocean-anim">
        <svg class="hero__wave" viewBox="0 0 1440 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="waveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#0d1428"/>
              <stop offset="40%" stop-color="#0a1f4e"/>
              <stop offset="80%" stop-color="#0d2d6b"/>
              <stop offset="100%" stop-color="#0a1f4e"/>
            </linearGradient>
            <linearGradient id="waveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#041020"/>
              <stop offset="50%" stop-color="#07193a"/>
              <stop offset="100%" stop-color="#041020"/>
            </linearGradient>
          </defs>
          <path class="hero__wave-path wave1" fill="url(#waveGrad1)" opacity="0.6"
            d="M0,160 C180,120 360,200 540,160 C720,120 900,200 1080,160 C1260,120 1440,160 1440,160 L1440,400 L0,400 Z"/>
          <path class="hero__wave-path wave2" fill="url(#waveGrad2)" opacity="0.4"
            d="M0,200 C240,160 480,240 720,200 C960,160 1200,220 1440,200 L1440,400 L0,400 Z"/>
          <path class="hero__wave-path wave3" fill="#04060f" opacity="0.5"
            d="M0,260 C200,230 400,280 600,260 C800,240 1000,270 1200,260 C1320,254 1380,260 1440,260 L1440,400 L0,400 Z"/>
        </svg>
      </div>
      <div class="hero__particles" id="heroParticles"></div>
    </div>

    <div class="container hero__content">
      <div class="hero__top anim-on-scroll">
        <div class="live-badge">
          <span class="live-dot"></span>
          Live Climate Intelligence
        </div>
      </div>

      <h1 class="hero__title anim-on-scroll anim-delay-1">
        El Niño <span class="hero__title-year">2026–27</span>
      </h1>
      <p class="hero__subtitle anim-on-scroll anim-delay-2">
        What Does It Mean for Agriculture?
      </p>
      <p class="hero__desc anim-on-scroll anim-delay-3">
        A live ESS assessment connecting Pacific Ocean climate signals with rainfall,
        temperature, water demand and vegetation response across our regions / countries of interest.
      </p>

      <div class="hero__cards anim-on-scroll anim-delay-4" id="heroCards">
        <div class="hero__card hero__card--status glass-card" id="cardStatus">
          <div class="hero__card-label">ENSO Status ${infoTrigger('hero-status')}</div>
          <div class="hero__card-value" id="cardStatusValue">
            <span class="skeleton" style="width:100px;height:32px;display:inline-block"></span>
          </div>
        </div>
        <div class="hero__card glass-card" id="cardRONI">
          <div class="hero__card-label">RONI Index ${infoTrigger('hero-roni')}</div>
          <div class="hero__card-value" id="cardRONIValue">
            <span class="skeleton" style="width:70px;height:32px;display:inline-block"></span>
          </div>
          <div class="hero__card-sub" id="cardRONISub">3-month running mean</div>
        </div>
        <div class="hero__card glass-card" id="cardNino">
          <div class="hero__card-label">Niño 3.4 ${infoTrigger('hero-nino34')}</div>
          <div class="hero__card-value" id="cardNinoValue">
            <span class="skeleton" style="width:70px;height:32px;display:inline-block"></span>
          </div>
          <div class="hero__card-sub" id="cardNinoSub">SST Anomaly (°C)</div>
        </div>
        <div class="hero__card glass-card" id="cardUpdated">
          <div class="hero__card-label">Latest Update ${infoTrigger('hero-updated')}</div>
          <div class="hero__card-value hero__card-value--sm" id="cardUpdatedValue">
            <span class="skeleton" style="width:90px;height:24px;display:inline-block"></span>
          </div>
          <div class="hero__card-sub">Source: NOAA CPC</div>
        </div>
      </div>

      ${infoPanel('hero-status', {
    title: 'ENSO Status',
    define: [
      { term: 'What is El Niño?', text: 'El Niño is a climate condition in which parts of the central and eastern tropical Pacific Ocean become unusually warm. This can influence atmospheric circulation and alter the probability of rainfall and temperature patterns in many parts of the world.' },
      { term: 'What is La Niña?', text: 'La Niña is the opposite phase, when the same part of the tropical Pacific becomes unusually cool.' },
      { term: 'What is ENSO?', text: 'ENSO stands for El Niño Southern Oscillation. It is the overall climate system that includes El Niño, La Niña and Neutral conditions.' },
    ],
    meaning: 'Loading the current status…',
    implication: 'El Niño can influence the probability of rainfall and temperature changes affecting, but it does not determine weather by itself. Agricultural effects depend on the actual rainfall, temperature, water availability and crop conditions experienced within agriculture.',
    takeaway: 'El Niño is an important global climate signal. It is a reason to monitor more closely, not by itself a prediction of agricultural loss.',
  })}

      ${infoPanel('hero-roni', {
    title: 'RONI Index',
    define: [
      { term: 'What is RONI?', text: 'RONI (Relative Oceanic Niño Index) is an index used to describe El Niño and La Niña conditions while taking account of temperature conditions across the wider tropical oceans.' },
      { term: 'In simple language', text: 'A positive RONI indicates conditions on the El Niño side, while a negative RONI indicates conditions on the La Niña side. The farther the value moves away from neutral, the stronger the Pacific climate signal may be.' },
      { term: 'Scale', text: '<span class="info-scale">La Niña &larr; Neutral &rarr; El Niño</span>' },
    ],
    meaning: 'Loading the current value…',
    implication: 'RONI itself does not measure crop stress. It tells us about the strength of a large scale climate driver. We therefore need to examine rainfall, temperature, water demand, soil moisture and vegetation response separately.',
    takeaway: 'RONI tells us about the climate driver. Observations tell us about the impact.',
  })}

      ${infoPanel('hero-nino34', {
    title: 'Niño 3.4 SST Anomaly',
    define: [
      { term: 'What is Niño 3.4?', text: 'Niño 3.4 is an important area of the tropical Pacific Ocean that scientists monitor to understand the development and strength of El Niño and La Niña.' },
      { term: 'What is SST?', text: 'SST means Sea Surface Temperature, which is the temperature at the ocean surface.' },
      { term: 'What is an SST anomaly?', text: 'An SST anomaly shows how much warmer or cooler the ocean is than usual for that place and time of year. A reading of +1.8°C means the water is about 1.8°C warmer than expected, not that its actual temperature is 1.8°C.' },
    ],
    meaning: 'Loading the current value…',
    implication: 'This Pacific warming can influence large scale atmospheric circulation, which may subsequently influence regional rainfall and temperature patterns. It is therefore an upstream climate signal rather than a direct measurement of agriculture.',
    takeaway: 'The Pacific is unusually warm. Now we need to determine whether and how is responding.',
  })}

      ${infoPanel('hero-updated', {
    title: 'Latest Update',
    meaning: 'This shows the newest monthly NOAA observation available. RONI is a three-month mean and can lag behind the Niño 3.4 calendar-month reading, so each metric keeps its own period label.',
    source: 'Source: NOAA Climate Prediction Center (CPC)',
  })}

      <div class="hero__scroll-cue anim-on-scroll anim-delay-4">
        <span>Explore the Dashboard</span>
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path d="M12 5v14M5 12l7 7 7-7"/>
        </svg>
      </div>
    </div>
  `;

  injectHeroStyles();
  initParticles(section);

  // Load live data
  fetchCurrentStatus().then(status => {
    populateHeroCards(section, status);
  });

  return section;
}

const SEASON_BY_MONTH_INDEX = {
  1: 'DJF', 2: 'JFM', 3: 'FMA', 4: 'MAM', 5: 'AMJ', 6: 'MJJ',
  7: 'JJA', 8: 'JAS', 9: 'ASO', 10: 'SON', 11: 'OND', 12: 'NDJ',
};

function populateHeroCards(section, status) {
  const statusEl = section.querySelector('#cardStatusValue');
  const roniEl = section.querySelector('#cardRONIValue');
  const ninoEl = section.querySelector('#cardNinoValue');
  const updEl = section.querySelector('#cardUpdatedValue');
  const roniSubEl = section.querySelector('#cardRONISub');
  const ninoSubEl = section.querySelector('#cardNinoSub');

  // NOAA's status is e.g. "El Niño Advisory" / "La Niña Watch" / "ENSO-Neutral" — classify by what it contains
  const cls = status.status.includes('El Niño') ? 'el-nino' : status.status.includes('La Niña') ? 'la-nina' : 'neutral';

  const roniVal = parseFloat(status.roni);
  const ninoVal = parseFloat(status.nino34);
  const roniSign = roniVal >= 0 ? '+' : '';
  const ninoSign = ninoVal >= 0 ? '+' : '';

  statusEl.innerHTML = `<span class="status-badge status-badge--${cls}">${status.status}</span>`;
  roniEl.innerHTML = `<span class="metric__value metric__value--warm">${roniSign}${status.roni}°C</span>`;
  ninoEl.innerHTML = `<span class="metric__value metric__value--warm">${ninoSign}${status.nino34}°C</span>`;
  // Show the newest monthly observation; the three-month RONI mean can lag it.
  updEl.innerHTML = `<span class="hero__card-date">${status.nino34Updated || status.updated}</span>`;

  if (roniSubEl && status.updatedDate) {
    const season = SEASON_BY_MONTH_INDEX[status.updatedDate.getMonth() + 1];
    roniSubEl.textContent = season ? `3-month running mean · ${season}` : '3-month running mean';
  }

  if (ninoSubEl) {
    ninoSubEl.textContent = status.nino34Updated
      ? `SST Anomaly (°C) · ${status.nino34Updated}`
      : 'SST Anomaly (°C)';
  }

  // Add glow to status card if El Niño
  if (cls === 'el-nino') {
    section.querySelector('#cardStatus').style.borderColor = 'rgba(249,115,22,0.3)';
    section.querySelector('#cardStatus').style.boxShadow = '0 0 32px rgba(249,115,22,0.1)';
  }

  // Fill in the dynamic "what does this mean" sentences now the real values are known
  const statusMeaning = status.status.includes('El Niño')
    ? `NOAA's ENSO Alert System status is <strong>${status.status}</strong>${status.bulletinIssued ? ` (bulletin of ${longDate(status.bulletinIssued)})` : ''}. An El Niño Advisory means that El Niño conditions are present and expected to continue.`
    : status.status.includes('La Niña')
      ? `A La Niña Advisory means that La Niña conditions are present. The latest status is <strong>${status.status}</strong>.`
      : `Neutral means the tropical Pacific is close to its long-term average. Neither El Niño nor La Niña conditions are currently present.`;
  setInfoMeaning('hero-status', statusMeaning);

  const roniMeaning = !Number.isNaN(roniVal)
    ? (roniVal >= 0.5
      ? `The current ${roniSign}${status.roni}°C RONI indicates that the tropical Pacific is experiencing a significant El Niño related warming signal.`
      : roniVal <= -0.5
        ? `The current ${status.roni}°C RONI indicates that the tropical Pacific is experiencing a significant La Niña related cooling signal.`
        : `The current ${roniSign}${status.roni}°C RONI is close to neutral. The tropical Pacific is near its long-term average.`)
    : 'RONI value currently unavailable.';
  setInfoMeaning('hero-roni', roniMeaning);

  const nino34Meaning = !Number.isNaN(ninoVal)
    ? `The current ${ninoSign}${status.nino34}°C reading means the Niño 3.4 region is approximately ${Math.abs(ninoVal).toFixed(2)}°C ${ninoVal >= 0 ? 'warmer' : 'cooler'} than usual for this time of year. It is an anomaly, not the ocean's actual temperature. This card uses the monthly OISST index, while NOAA's bulletin uses the relative Niño 3.4 index, so the two readings can differ.`
    : 'Niño 3.4 anomaly currently unavailable.';
  setInfoMeaning('hero-nino34', nino34Meaning);
}

function initParticles(section) {
  const container = section.querySelector('#heroParticles');
  for (let i = 0; i < 24; i++) {
    const p = document.createElement('div');
    p.className = 'hero__particle';
    p.style.cssText = `
      left: ${Math.random() * 100}%;
      top: ${Math.random() * 100}%;
      width: ${Math.random() * 3 + 1}px;
      height: ${Math.random() * 3 + 1}px;
      animation-delay: ${Math.random() * 4}s;
      animation-duration: ${4 + Math.random() * 4}s;
      opacity: ${0.1 + Math.random() * 0.3};
    `;
    container.appendChild(p);
  }
}

function injectHeroStyles() {
  if (document.getElementById('hero-styles')) return;
  const style = document.createElement('style');
  style.id = 'hero-styles';
  style.textContent = `
    .hero {
      position: relative;
      min-height: 100vh;
      display: flex;
      align-items: center;
      overflow: hidden;
      background: radial-gradient(ellipse 120% 80% at 60% 40%, #0d2d6b 0%, #04060f 70%);
    }

    .hero__bg {
      position: absolute;
      inset: 0;
      pointer-events: none;
      overflow: hidden;
    }

    .hero__ocean-anim {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60%;
    }

    .hero__wave {
      width: 200%;
      height: 100%;
      animation: waveFlow 12s ease-in-out infinite;
    }

    .wave1 { animation-duration: 14s; }
    .wave2 { animation-duration: 10s; animation-direction: reverse; }
    .wave3 { animation-duration: 16s; }

    .hero__particles {
      position: absolute;
      inset: 0;
    }

    .hero__particle {
      position: absolute;
      border-radius: 50%;
      background: var(--clr-accent);
      animation: float var(--dur, 5s) ease-in-out infinite;
    }

    .hero__content {
      position: relative;
      z-index: var(--z-raised);
      padding-top: 8rem;
      padding-bottom: 6rem;
    }

    .hero__top {
      display: flex;
      align-items: center;
      gap: var(--space-4);
      margin-bottom: var(--space-8);
      flex-wrap: wrap;
    }

    .hero__logo {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-family: var(--font-mono);
      font-size: var(--text-sm);
      color: var(--clr-text-tertiary);
    }

    .hero__logo-img {
      height: 22px;
      width: auto;
      display: block;
    }

    .hero__logo-dot { color: var(--clr-text-tertiary); font-size: 0.5em; }

    .hero__title {
      font-family: var(--font-display);
      font-size: clamp(3.5rem, 10vw, 8rem);
      font-weight: var(--weight-black);
      line-height: 1;
      letter-spacing: -0.03em;
      color: var(--clr-text-primary);
      margin-bottom: var(--space-4);
    }

    .hero__title-year {
      background: linear-gradient(135deg, #f97316, #fbbf24);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .hero__subtitle {
      font-family: var(--font-display);
      font-size: clamp(var(--text-xl), 3vw, var(--text-3xl));
      font-weight: var(--weight-medium);
      color: var(--clr-text-secondary);
      margin-bottom: var(--space-6);
      line-height: 1.3;
    }

    .hero__desc {
      font-size: var(--text-lg);
      color: var(--clr-text-secondary);
      max-width: 620px;
      line-height: 1.7;
      margin-bottom: var(--space-12);
    }

    .hero__cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: var(--space-4);
      margin-bottom: var(--space-16);
    }

    .hero__card {
      padding: var(--space-5) var(--space-6);
    }

    .hero__card-label {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--clr-text-tertiary);
      margin-bottom: var(--space-2);
    }

    .hero__card-value {
      font-family: var(--font-display);
      font-size: var(--text-2xl);
      font-weight: var(--weight-bold);
      margin-bottom: var(--space-1);
      min-height: 36px;
    }

    .hero__card-value--sm {
      font-size: var(--text-xl);
    }

    .hero__card-date {
      color: var(--clr-text-primary);
      font-weight: var(--weight-bold);
    }

    .hero__card-sub {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
    }

    .hero__scroll-cue {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-size: var(--text-sm);
      color: var(--clr-text-tertiary);
      animation: arrowFlow 2.5s ease-in-out infinite;
    }

    @media (max-width: 1024px) {
      .hero__cards { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 640px) {
      .hero__cards { grid-template-columns: 1fr; }
      .hero__content { padding-top: 6rem; }
    }
  `;
  document.head.appendChild(style);
}
